import React, { useState } from "react";
import * as LocalStore from "../services/LocalStore";
import { useToast } from "./ToastProvider";

type ImportPayload = { calendars?: any[]; contracts?: any[] };

type PreviewItem = {
  name: string;
  data?: any;
  checked: boolean;
  imported?: boolean;
  willDelete?: boolean;
  disabled?: boolean;
  diffCount?: number;
};

export default function ImportExport() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [calPreview, setCalPreview] = useState<PreviewItem[]>([]);
  const [contractPreview, setContractPreview] = useState<PreviewItem[]>([]);
  const toast = useToast();

  async function doExport() {
    const cals = await LocalStore.getCalendars();
    const contracts = await LocalStore.getContracts();
    const payload: ImportPayload = { calendars: cals, contracts };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "times-export.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function onFile(f: File | null) {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(String(e.target?.result));
        buildPreview(parsed as ImportPayload);
      } catch (err) {
        toast.show("Invalid JSON file", "error");
      }
    };
    reader.readAsText(f);
  }

  async function loadSample() {
    try {
      const r = await fetch("/sample-import.json");
      if (!r.ok) {
        toast.show("Failed to load sample data", "error");
        return;
      }
      const parsed = await r.json();
      buildPreview(parsed as ImportPayload);
    } catch (err) {
      toast.show("Failed to load sample data", "error");
    }
  }

  async function clearAllData() {
    const ok = window.confirm(
      "This will permanently delete all calendars and contracts from local storage. This cannot be undone. Continue?"
    );
    if (!ok) return;
    // delete calendars
    const existingCals = await LocalStore.getCalendars();
    let calCount = 0;
    for (const c of existingCals) {
      if (c?.name) {
        const deleted = await LocalStore.deleteCalendar(c.name);
        if (deleted) calCount++;
      }
    }
    // delete contracts
    const existingContracts = await LocalStore.getContracts();
    let contractCount = 0;
    for (const ct of existingContracts) {
      if (ct?.name) {
        const deleted = await LocalStore.deleteContract(ct.name);
        if (deleted) contractCount++;
      }
    }
    // clear preview UI
    setPreviewOpen(false);
    setCalPreview([]);
    setContractPreview([]);
    toast.show(
      `Cleared ${calCount} calendar(s) and ${contractCount} contract(s).`,
      "info"
    );
  }

  async function buildPreview(payload: ImportPayload) {
    // compute calendars preview
    const existingCals = await LocalStore.getCalendars();
    const existingContracts = await LocalStore.getContracts();

    const calNames = new Set((payload.calendars ?? []).map((c) => c.name));
    const contractNames = new Set((payload.contracts ?? []).map((c) => c.name));

    const calItems: PreviewItem[] = [];

    // incoming
    for (const c of payload.calendars ?? []) {
      const existing = existingCals.find((x) => x.name === c.name);
      const diff = existing
        ? JSON.stringify(existing) === JSON.stringify(c)
          ? 0
          : 1
        : 1;
      calItems.push({
        name: c.name,
        data: c,
        checked: true,
        imported: !!existing,
        disabled: diff === 0,
        diffCount: diff,
      });
    }
    // existing not in incoming -> will be deleted
    for (const e of existingCals) {
      if (!calNames.has(e.name))
        calItems.push({
          name: e.name,
          data: e,
          checked: true,
          willDelete: true,
          imported: false,
          disabled: false,
          diffCount: 1,
        });
    }

    const contractItems: PreviewItem[] = [];
    for (const c of payload.contracts ?? []) {
      const existing = existingContracts.find((x) => x.name === c.name);
      const diff = existing
        ? JSON.stringify(existing) === JSON.stringify(c)
          ? 0
          : 1
        : 1;
      contractItems.push({
        name: c.name,
        data: c,
        checked: true,
        imported: !!existing,
        disabled: diff === 0,
        diffCount: diff,
      });
    }
    for (const e of existingContracts) {
      if (!contractNames.has(e.name))
        contractItems.push({
          name: e.name,
          data: e,
          checked: true,
          willDelete: true,
          imported: false,
          disabled: false,
          diffCount: 1,
        });
    }

    setCalPreview(calItems);
    setContractPreview(contractItems);
    setPreviewOpen(true);
  }

  async function confirmImport() {
    // Save checked imported items and delete checked willDelete items
    for (const it of calPreview) {
      if (it.disabled) continue;
      if (it.willDelete && it.checked) {
        await LocalStore.deleteCalendar(it.name);
      } else if (it.data && it.checked && !it.willDelete) {
        await LocalStore.saveCalendar(it.data);
      }
    }
    for (const it of contractPreview) {
      if (it.disabled) continue;
      if (it.willDelete && it.checked) {
        await LocalStore.deleteContract(it.name);
      } else if (it.data && it.checked && !it.willDelete) {
        await LocalStore.saveContract(it.data);
      }
    }
    setPreviewOpen(false);
    setCalPreview([]);
    setContractPreview([]);
  }

  return (
    <div>
      <h1>Import / Export</h1>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={doExport}>Export</button>
        <input
          type="file"
          accept="application/json"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <button onClick={loadSample}>Load sample data</button>
        <button
          onClick={clearAllData}
          style={{ marginLeft: 12, color: "#b91c1c" }}
        >
          Clear All Data
        </button>
      </div>

      {previewOpen && (
        <div style={{ marginTop: 12 }}>
          <h3>Preview</h3>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <h4>Calendars</h4>
              <ul>
                {calPreview.map((p) => (
                  <li key={p.name}>
                    <input
                      type="checkbox"
                      checked={p.checked}
                      disabled={p.disabled}
                      onChange={(e) =>
                        setCalPreview((prev) =>
                          prev.map((x) =>
                            x.name === p.name
                              ? { ...x, checked: e.target.checked }
                              : x
                          )
                        )
                      }
                    />
                    <strong style={{ marginLeft: 8 }}>{p.name}</strong>
                    {p.willDelete && (
                      <span style={{ marginLeft: 8, color: "#b91c1c" }}>
                        (will be deleted)
                      </span>
                    )}
                    {p.disabled && (
                      <span style={{ marginLeft: 8, color: "#6b7280" }}>
                        (No Changes)
                      </span>
                    )}
                    {p.diffCount! > 0 && !p.disabled && (
                      <span style={{ marginLeft: 8, color: "#0b74d1" }}>
                        ({p.diffCount} Changes)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4>Contracts</h4>
              <ul>
                {contractPreview.map((p) => (
                  <li key={p.name}>
                    <input
                      type="checkbox"
                      checked={p.checked}
                      disabled={p.disabled}
                      onChange={(e) =>
                        setContractPreview((prev) =>
                          prev.map((x) =>
                            x.name === p.name
                              ? { ...x, checked: e.target.checked }
                              : x
                          )
                        )
                      }
                    />
                    <strong style={{ marginLeft: 8 }}>{p.name}</strong>
                    {p.willDelete && (
                      <span style={{ marginLeft: 8, color: "#b91c1c" }}>
                        (will be deleted)
                      </span>
                    )}
                    {p.disabled && (
                      <span style={{ marginLeft: 8, color: "#6b7280" }}>
                        (No Changes)
                      </span>
                    )}
                    {p.diffCount! > 0 && !p.disabled && (
                      <span style={{ marginLeft: 8, color: "#0b74d1" }}>
                        ({p.diffCount} Changes)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <button onClick={confirmImport}>Confirm Import</button>
            <button onClick={() => setPreviewOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
